import Vue from 'vue';
import ApiClient from '../client';
const path = require('path');

/**
 * User session.
 * @class
 * @property {boolean} isUserAuthenticated
 * @property {Object} userData
 * @property {boolean} isSuperuser
 * @property {Array<*>} getUsergroupRoles
 * @property {boolean} hasUsergroupRole
 * @property {boolean} isDelegatedAdmin
 * @property {Array<*>} getUserAdminRoles
 * @property {Array<*>} getUserEditorRoles
 */
class UserSession {
  isUserAuthenticated = false;

  userData = {
    is_superuser: false,
    token: null,
  };

  /** @returns {boolean} */
  isSuperuser() {
    return Boolean(this.userData && this.userData.is_superuser);
  }

  /** @returns {Array<*>} */
  getUsergroupRoles() {
    const u = this.userData;
    if (!u) {
      return [];
    }
    return u.usergroup_roles || [];
  }

  /**
   * @param {number} roleValue
   * @returns {boolean}
   */
  hasUsergroupRole(roleValue) {
    return this.getUsergroupRoles().some((el) => el.role === roleValue);
  }

  isDelegatedAdminMultipleOrganisations() {
    return this.getUsergroupRoles().filter((el) => el.role === 2).length > 1;
  }
  /** @returns {boolean} */
  isDelegatedAdmin() {
    return this.hasUsergroupRole(2);
  }
   /** @returns {boolean} */
   isEditor() {
    return this.hasUsergroupRole(1);
  }

  /** @returns {Array<*>} */
  getUserAdminRoles() {
    return this.getUsergroupRoles().filter((el) => el.role === 2);
  }

  /** @returns {Array<*>} */
  getUserEditorRoles() {
    return this.getUsergroupRoles().filter((el) => el.role >= 1);
  }
}

/**
 * User session observable.
 * @type {Vue.Observable<UserSession>}
 */
export const userSession = Vue.observable(new UserSession());

class LoginAPI extends ApiClient {

  constructor() {
    super();
  }

  async getUserSession() {
    try {
      const response = await this.get(this.LOGIN_API_PATH);
      if (response.status === 200) {
        const data = response.data;
        if (data && typeof data === 'object' && data.user != null) {
          return data;
        }
        return false;
      }
    } catch (err) {
      if (err.response && err.response.data.code && err.response.data.code === 'terms-required') {
        window.location.href = `${process.env.VUE_APP_LOGIN_TERMS_ROUTE}?next=${window.location.pathname}${window.location.hash}`;
      }
      return false;
    }
  }

  /**
   * Fetches login state from the API and updates {@link userSession}.
   * On failure, redirects to the app login route.
   * @returns {Promise<Record<string, *>|false>}
   */
  async authenticate() {
    const authentication = await this.getUserSession();
    if (authentication) {
      userSession.isUserAuthenticated = authentication.authenticated
        ? authentication.authenticated
        : false;
      if (authentication.user) {
        userSession.userData = authentication.user;
      }
      return authentication;
    }
    window.location.href = `${process.env.VUE_APP_LOGIN_ROUTE}?next=${window.location.pathname}${window.location.hash}`;
  }

  async userLogout() {
    const url = path.join(
      this.USER_LOGIN_API_PATH,
      '/signout'
    );
    const response = await this.get(url);
    return response.data;
  }

}

const loginAPI = new LoginAPI();
export default loginAPI;
